import React from 'react';
import { Box, Typography, Popover } from '@mui/material';
import { SparkButton } from 'sharedComponents/UI';
import { Toolbar } from 'external/TipTapEditor/components/ui/Toolbar';
import VideoPlayer from './VideoPlayer';
import {
  getYouTubeVideoId,
  loomRegex,
} from 'components/CreateSpark/SparkBuilderDrawers/SparkBuilderSettingsFiles/ImagesAndMedia/VideoLoom';
import AspectRatioIcon from './SVG/AspectRatioIcon';
import AROneByOneIcon from './SVG/AROneByOneIcon';
import ARSixteenByNineIcon from './SVG/ARSixteenByNineIcon';
import ARFourByThreeIcon from './SVG/ARFourByThreeIcon';
import ARThreeByFourIcon from './SVG/ARThreeByFourIcon';
import ARNineBySixteenIcon from './SVG/ARNineBySixteenIcon';
import { usePopover } from 'hooks/common';
import type { ListOptionsType } from 'types/common/DropdownOption';
import BoardNodeTranscription from './BoardNodeTranscription';

type VideoNodeProps = {
  initialContent: string | undefined;
  setNodeSpecificOptions?: React.Dispatch<
    React.SetStateAction<ListOptionsType[]>
  >;
  onError: ((errorMessage: string) => void) | undefined;
};

type VideoNodeFileType = {
  fileSize: string;
  fileType: string;
  id: string;
  isDeleted: boolean;
  originalFileName: string;
  url: string;
};

type AspectRatioCategory = 'SQUARE' | 'HORIZONTAL' | 'VERTICAL';

interface AspectRatioOption {
  id: string;
  label: string;
  icon: React.ComponentType;
  aspectRatio: string;
  category: AspectRatioCategory;
}

type GroupedOptions = Record<AspectRatioCategory, AspectRatioOption[]>;

const IconWrapper = ({ children }: { children: React.ReactNode }) => (
  <Box
    width='18px'
    height='18px'
    bgcolor='#F9F9F9'
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 1,
    }}
  >
    {children}
  </Box>
);

const aspectRatioOptions: AspectRatioOption[] = [
  {
    id: 'square',
    label: 'Square 1:1',
    icon: AROneByOneIcon,
    aspectRatio: '1/1',
    category: 'SQUARE',
  },
  {
    id: 'horizontal-16-9',
    label: '16:9',
    icon: ARSixteenByNineIcon,
    aspectRatio: '16/9',
    category: 'HORIZONTAL',
  },
  {
    id: 'horizontal-4-3',
    label: '4:3',
    icon: ARFourByThreeIcon,
    aspectRatio: '4/3',
    category: 'HORIZONTAL',
  },
  {
    id: 'vertical-3-4',
    label: '3:4',
    icon: ARThreeByFourIcon,
    aspectRatio: '3/4',
    category: 'VERTICAL',
  },
  {
    id: 'vertical-9-16',
    label: '9:16',
    icon: ARNineBySixteenIcon,
    aspectRatio: '9/16',
    category: 'VERTICAL',
  },
];

function VideoNode({
  initialContent,
  setNodeSpecificOptions,
  onError,
}: VideoNodeProps) {
  const nodeData: VideoNodeFileType[] = initialContent
    ? JSON.parse(initialContent)?.files
    : null;
  const MemoButton = React.memo(Toolbar.Button);
  const [selectedAspectRatio, setSelectedAspectRatio] =
    React.useState<AspectRatioOption | null>(null);
  const {
    anchorEl,
    open: isPopoverOpen,
    closePopover,
    openPopover,
  } = usePopover();

  const handleAspectRatioSelect = (option: AspectRatioOption): void => {
    setSelectedAspectRatio(option);
    closePopover();
  };

  const renderTriggerContent = (): React.ReactElement => {
    if (!selectedAspectRatio) {
      return <AspectRatioIcon />;
    }
    return <Typography variant='body2'>{selectedAspectRatio.label}</Typography>;
  };

  const groupedOptions = aspectRatioOptions.reduce<GroupedOptions>(
    (acc, option) => {
      if (!acc[option.category]) {
        acc[option.category] = [];
      }
      acc[option.category].push(option);
      return acc;
    },
    {} as GroupedOptions
  );

  React.useEffect(() => {
    setNodeSpecificOptions?.([
      {
        label: 'Aspect Ratio',
        tag: 'aspectRatio',
        onClick: () => {},
        icon: (
          <>
            <SparkButton onClick={openPopover} sx={{ color: 'black' }}>
              {renderTriggerContent()}
            </SparkButton>
            <Popover
              open={isPopoverOpen}
              anchorEl={anchorEl}
              onClose={closePopover}
              sx={{ zIndex: 9999 }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <Toolbar.Wrapper
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  minWidth: '150px',
                }}
              >
                {Object.entries(groupedOptions).map(
                  ([category, options]: [string, AspectRatioOption[]]) => (
                    <React.Fragment key={category}>
                      <Typography
                        variant='body1'
                        sx={{ fontWeight: 'bold', mt: 1, mb: 0.5 }}
                      >
                        {category}
                      </Typography>
                      {options.map((option) => {
                        const IconComponent = option.icon;
                        return (
                          <MemoButton
                            key={option.id}
                            onClick={() => handleAspectRatioSelect(option)}
                            style={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'flex-start',
                            }}
                          >
                            <IconWrapper>
                              <IconComponent />
                            </IconWrapper>
                            <Typography
                              sx={{ marginLeft: 0.5 }}
                              component='span'
                              variant='body2'
                            >
                              {option.label}
                            </Typography>
                          </MemoButton>
                        );
                      })}
                    </React.Fragment>
                  )
                )}
              </Toolbar.Wrapper>
            </Popover>
          </>
        ),
      },
    ]);
  }, [selectedAspectRatio, isPopoverOpen]);

  const youtubeMatch = getYouTubeVideoId(nodeData?.[0]?.url);
  const loomMatch = nodeData?.[0]?.url?.match(loomRegex);
  const videoURL = youtubeMatch
    ? `https://www.youtube.com/embed/${youtubeMatch}`
    : loomMatch
      ? `https://www.loom.com/embed/${loomMatch[0].split('/').pop()}`
      : nodeData?.[0]?.url;

  const isExternalVideo = Boolean(youtubeMatch || loomMatch);

  return (
    <Box p={2} pt={0} onClick={(event) => event.stopPropagation()}>
      <VideoPlayer
        videoURL={videoURL}
        isExternalVideo={isExternalVideo}
        aspectRatio={selectedAspectRatio?.aspectRatio}
      />
      <BoardNodeTranscription fileID={'a90b5fc0-931b-443d-993d-785bc2f67422'} />
    </Box>
  );
}

export default VideoNode;
